import { types, getParent, getRoot, flow, applySnapshot } from "mobx-state-tree";
import { createFilter } from "react-search-input";
import { db, auth } from "../firebase.js";
import { actions } from "./_common.js";
import { entries } from "mobx";
import FirestoreDate from "./FirestoreDate";
import { values } from "mobx";

export const roles = [
  {
    value: "admin",
    label: "Admin"
  },
  {
    value: "creator",
    label: "Creator"
  },
  {
    value: "mentor",
    label: "Begeleider"
  },
  {
    value: "auth",
    label: "Authenticated"
  },
  {
    value: "anonymous",
    label: "Anonymous"
  }
];

let refMeta = db.collection("usersMeta");

export const User = types.compose(
  types
    .model("user", {
      uid: types.optional(types.string, ""),
      email: types.maybeNull(types.string, ""),
      emailVerified: types.optional(types.boolean, false),
      password: types.optional(types.string, ""),
      disabled: types.optional(types.boolean, false),
      ip: types.maybe(types.string),
      roles: types.optional(types.frozen([]), []),
      groups: types.optional(types.frozen([]), []),
      //defaultGroup: types.maybeNull(types.string),
      language: types.optional(types.string, "nl_BE"),
      createdOn: types.maybeNull(FirestoreDate),
      creationTime: types.maybeNull(FirestoreDate), //deprecated
      lastSignInTime: types.maybeNull(FirestoreDate), //deprecated
      clusters: types.optional(types.frozen([]), []),
      isAnonymous: types.optional(types.boolean, false),
      boarding: types.optional(types.boolean, true),
      disclaimer: types.optional(types.boolean, false),
      showDisclaimerInfo: types.optional(types.boolean, false)
    })
    .actions(self => ({
      save: flow(function* create() {
        let root = getRoot(self);
        root.$alert.add("loading");
        let validations = root.$validation.validateForm("user-edit");
        if (validations.size === 0) {
          yield refMeta.doc(self.uid).set({
            roles: self.roles,
            groups: self.groups,
            clusters: self.clusters,
            language: self.language,
            email: self.email,
            boarding: self.boarding,
            emailVerified: self.emailVerified,
            createdOn: self.createdOn || self.creationTime
          });
          getParent(self).setToList(self.uid, prepareUser(self));
          root.$alert.delete("loading");
        }
      }),
      delete: flow(function* () {
        const { $alert, $router } = getRoot(self);
        const uid = self.uid;
        $alert.add("loading");
        let response = yield _fetch(
          getRoot(self).$config.functions_url + "/deleteUser",
          JSON.stringify(self.toJSON())
        );
        response = yield response.json();
        if (response.error) {
          $alert.add("error", { label: response.error });
        }
        yield refMeta.doc(uid).delete();
        $alert.delete("loading");
        $router.push("/user");
      }),
      addGroup(groupKey) {
        let groups = JSON.parse(JSON.stringify(self.groups)) || [];
        groups.push(groupKey);
        self.groups = groups;
      }
    }))
    .views(self => ({
      get groupNames() {
        if (getRoot(self).$group.list.size) {
          const groupsArr = self.groupsArr.map(
            g => getRoot(self).$group.list.has(g._key) && getRoot(self).$group.list.get(g._key).name
          );
          return groupsArr.length ? groupsArr.join(", ") : "Not found";
        } else {
          return "";
        }
      },
      get groupsArr() {
        const $group = getRoot(self).$group;
        if (!self.groups || !Array.isArray(self.groups)) return [];
        return self.groups.map(group =>
          $group.list.has(group) ? $group.list.get(group).toJSON() : { name: group, _key: group }
        );
      },
      get responses() {
        return values(getRoot(self).$respondent.list).filter(
          respondent => respondent.createdBy === self.uid
        );
      },
      get isAdmin() {
        return self.roles.indexOf("admin") > -1;
      },
      get isVerifiedCreator() {
        return self.emailVerified && self.roles.indexOf("creator") > -1;
      },
      get isVerifiedMentor() {
        return self.emailVerified && self.roles.indexOf("mentor") > -1;
      },
      get isVerifiedAuth() {
        return self.emailVerified && self.roles.indexOf("auth") > -1;
      },
      get isCreator() {
        return self.roles.indexOf("creator") > -1;
      },
      get isMentor() {
        return self.roles.indexOf("mentor") > -1;
      },
      get isAuth() {
        return self.roles.indexOf("auth") > -1;
      },
      get isPrivate() {
        return self.isAdmin || self.isVerifiedCreator;
      }
    })),
  actions
);

export const UserStore = types.compose(
  types
    .model("UserStore", {
      list: types.optional(types.map(User), {}),
      status: types.optional(types.string, ""),
      selected: types.maybeNull(User),
      sessionUser: types.optional(User, {}),
      storageKey: "KEY_FOR_LOCAL_STORAGE",
      registerForCluster: types.maybeNull(types.string),
      registerStep: types.optional(types.string, "register"),
      enableRegister: types.optional(types.boolean, false),
      enablePasswordReset: types.optional(types.boolean, false),
      enableSignIn: types.optional(types.boolean, false),
      filterSearch: types.optional(types.string, ""),
      userToDelete: types.maybeNull(User)
    })
    .volatile(self => ({
      unsubscribe: types.function
    }))
    .actions(self => ({
      afterCreate() {
        auth.onAuthStateChanged(async user => {
          console.log("onAuthStateChanged", user);
          getRoot(self).$group.change("status", "pending");
          self.change("status", "pending");
          //user === null
          if (!user) {
            let group = getRoot(self).$router.getURLParameterByName("group");
            console.log("onAuthStateChanged: group", group);
            if (group) {
              await self.signInAnonymously();
            }
          }

          //user = null
          if (!user) {
            self.setSessionUser(user);
          }
          //user = anonymous
          if (user && user.isAnonymous) {
            self.setSessionUser(user);
          }
          //user = auth or mentor or admin
          if (user && !user.isAnonymous) {
            await self.setMetaData(user);
            if (!user.emailVerified) {
              self.sendVerificationMail(user);
              self.setSessionUser(user);
            } else {
              localStorage.removeItem("barometer-sendVerificationEmail");
              self.setSessionUser(user);
            }
          }
        });
      },
      sendVerificationMail: flow(function* (user) {
        user = { ...user, ...JSON.parse(window.localStorage.getItem("user")) };

        let sendVerificationEmail = JSON.parse(
          localStorage.getItem("barometer-sendVerificationEmail", true)
        );
        if (!sendVerificationEmail) {
          let response = yield _fetch(
            getRoot(self).$config.functions_url + "/sendVerificationEmail",
            JSON.stringify({
              email: user.email,
              uid: user.uid,
              group: user ? user.group : false
            })
          );
          if (response.status === 200) {
            response = yield response.json();
            getRoot(self).$alert.add("send", {
              label: response.message,
              time: 10000
            });
            localStorage.setItem("barometer-sendVerificationEmail", true);
          }
        } else {
          getRoot(self).$alert.add("send", {
            label: "Verificatie email reeds verstuurd naar: " + user.email,
            time: 10000
          });
        }
      }),
      setMetaData: flow(function* (user) {
        user = { ...user, ...JSON.parse(window.localStorage.getItem("user")) };

        console.log("setmeta");
        let snapshot = yield refMeta.doc(user.uid).get();
        if (snapshot.exists) return true;
        else {
          let roles = ["auth"];
          //if group data, then create group
          if (user.group) {
            roles.push("mentor");
            getRoot(self).$group.setSelected({
              _key: "new",
              name: user.group,
              createdBy: user.uid,
              createdByEmail: user.email,
              createdOn: new Date(),
              ...(user.educationLevel && {
                educationLevel: user.educationLevel
              }),
              ...(user.brugfiguur && {
                brugfiguur: user.brugfiguur
              })
            });
            user.group = yield getRoot(self).$group.selected.fsSave();
          }
          // create meta
          let metaData = {
            roles,
            groups: user.group ? user.group.id.split() : null,
            email: user.email,
            createdOn: new Date(),
            clusters: user.cluster ? user.cluster.split() : null,
            emailVerified: user.emailVerified
          };
          yield refMeta.doc(user.uid).set(metaData);
          //window.localStorage.removeItem("meta");
        }
      }),
      setSessionUser: flow(function* setSessionUser(user) {
        //user
        if (user && !user.disabled) {
          if (user.isAnonymous) {
            localStorage.removeItem("barometer-group"); // this is only necessary for anonymous users
            self.sessionUser = prepareUser(user);
          } else {
            const meta = yield refMeta.doc(user.uid).get();
            console.log("setSessionUser :", user, meta);
            //auth, mentor or admin
            if (meta.exists) {
              user = {
                ...user,
                ...meta.data()
              };
            } else {
              getRoot(self).$alert.add("No metadata found", {
                label: "No Metadata found"
              });
              console.error("No metadata found");
              self.signOut();
            }
            self.sessionUser = prepareUser(user);
            self.setToList(user.uid, prepareUser(user));
          }
        }
        self.change("status", "done");
      }),
      deletesessionUser() {
        self.sessionUser = {};
      },
      loadUser: flow(function* load(uid) {
        const { $alert, $config } = getRoot(self);
        $alert.add("loading");
        const response = yield _fetch(
          $config.functions_url + "/getUser",
          JSON.stringify({
            uid
          })
        );

        if (response.status === 200) {
          console.log("response :", response);
          try {
            const user = yield response.json();
            self.change(
              "selected",
              Object.assign({}, self.selected, {
                creationTime: prepareUser(user).creationTime,
                lastSignInTime: prepareUser(user).lastSignInTime,
                emailVerified: prepareUser(user).emailVerified
              })
            );
            getRoot(self).$alert.delete("loading");
            console.log("user :", user);
          } catch (error) {
            console.log("error", error);
          }
        } else {
          getRoot(self).$alert.add("status", {
            label: "Error" + response.status
          });
        }
      }),
      // load: flow(function* load() {
      //   const { $alert } = getRoot(self);
      //   $alert.add("loading");
      //   self.change("list", {});
      //   self.loadMeta();
      //   getRoot(self).$alert.delete("loading");
      // }),

      loadMeta(params) {
        const { $alert } = getRoot(self);
        $alert.add("loading");

        let refMeta = db.collection("usersMeta");
        if (params && params.groupKey) {
          refMeta = refMeta.where("groups", "array-contains", params.groupKey);
        }
        if (params && params.createdBy) {
          refMeta = refMeta.where("createdBy", "==", params.createdBy);
        }
        if (params && params.uid) {
          refMeta = refMeta.doc(params.uid);
        }

        self.unsubscribe = refMeta.onSnapshot(snapshot => {
          let list = {};
          snapshot.forEach(user => {
            let oldUser = self.list.get(user.id);
            list[user.id] = oldUser ? { ...oldUser, ...user.data() } : user.data();
            list[user.id].uid = user.id;
          });
          applySnapshot(self.list, list);
          $alert.delete("loading");
        });
        // const usersMeta = yield refMeta.get();
        // usersMeta.forEach(user => {
        //   let oldUser = self.list.get(user.id);
        //   let newUser = Object.assign({}, oldUser, {
        //     uid: user.id,
        //     ...user.data()
        //   });
        //   user.data().roles.indexOf("auth") > -1 &&
        //     self.setToList(user.id, newUser);
        // });
      },
      signIn: flow(function* signIn(email, password) {
        getRoot(self).change("$loading", true);
        console.log("email :", email);
        try {
          let response = yield auth.signInWithEmailAndPassword(email, password);
          self.setSessionUser({
            ...response.user
          });
          getRoot(self).$user.change("enableSignIn", false);

          getRoot(self).change("$loading", false);
        } catch (error) {
          getRoot(self).change("$loading", false);
          console.log("error :", error);
          getRoot(self).$alert.add("status", { label: error.code });
        }
      }),
      signInAnonymously: flow(function* () {
        yield auth.signInAnonymously().catch(function (error) {
          console.log("error :", error);
        });
        return Promise.resolve(true);
      }),
      register: flow(function* (user) {
        getRoot(self).$alert.add("loading");
        try {
          yield auth.createUserWithEmailAndPassword(user.email, user.password);
          localStorage.removeItem("barometer-sendVerificationEmail");
          localStorage.removeItem("user");
          user && window.localStorage.setItem("user", JSON.stringify(user));
          getRoot(self).$alert.delete("loading");
          return true;
        } catch (error) {
          getRoot(self).$alert.delete("loading");
          getRoot(self).$alert.add("error", { label: error.code });
        }
      }),
      signOut: flow(function* signOut() {
        yield auth.signOut();
        getRoot(self).$locale.setActiveLocale("nl_BE");
        self.deletesessionUser();
        getRoot(self).$router.push(getRoot(self).$config.home);
      }),
      passwordReset: flow(function* (email) {
        getRoot(self).$alert.add("loading");
        try {
          yield auth.sendPasswordResetEmail(email);
          getRoot(self).$alert.delete("loading");
          getRoot(self).$alert.add("success", {
            label: getRoot(self).$locale.t("user_password_reset_success") + email
          });
          self.change("enablePasswordReset", false);
        } catch (error) {
          getRoot(self).$alert.delete("loading");
          getRoot(self).$alert.add("error", { label: error.code });
        }
      })
    }))
    .views(self => ({
      getUserForQuestionKey(key) {
        const user = entries(self.list).find(user => String(user[1].questionKey) === String(key));
        return user
          ? {
              key: user[0],
              value: user[1]
            }
          : false;
      },
      get filteredList() {
        return values(self.list).filter(
          createFilter(self.filterSearch, ["email", "groupNames", "uid"])
        );
      }
    })),
  actions
);

async function _fetch(url, body = false, method = "POST") {
  try {
    const token = await auth.currentUser.getIdToken();
    let request = {};
    if (method) request["method"] = method;
    if (body) request["body"] = body;
    if (token)
      request["headers"] = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token
      };
    return fetch(url, request);
  } catch (error) {
    console.error(error);
    return false;
  }
}

function prepareUser(user) {
  return {
    email: user.email,
    emailVerified: user.emailVerified,
    boarding: user.boarding,
    clusters: user.clusters,
    disabled: user.disabled,
    isAnonymous: user.isAnonymous,
    creationTime: user.metadata && new Date(user.metadata.creationTime),
    lastSignInTime: user.metadata && new Date(user.metadata.lastSignInTime),
    uid: user.uid,
    roles: user.roles,
    groups: user.groups, //defaultGroup: user.groups ? user.groups.split(',')[0] : null,
    language: user.language,
    metadata: user.metadata,
    createdOn: user.createdOn
  };
}
